import { useState } from "react";
import clsx from "clsx";

import { Button } from "../base/Button";
import usePinnacleMappingMutations from "../../data/PinnacleMapping/usePinnacleMappingMutations";
import { usePinnacleSuggestions } from "../../data/PinnacleMapping/usePinnacleMapping";
import { confidenceMeta, describeConditions } from "./pinnacleMeta";

/**
 * Possible mappings for one item type. Nothing here is applied until someone adds it, at which point
 * it becomes an ordinary confirmed mapping.
 */
export function SuggestionList({ itemTypeId }: { itemTypeId: string }) {
  const suggestions = usePinnacleSuggestions(itemTypeId);
  const { acceptSuggestion, dismissSuggestion } = usePinnacleMappingMutations();
  const [addingAll, setAddingAll] = useState(false);

  const list = suggestions.data ?? [];
  const high = list.filter((s) => s.confidence === "High");

  if (suggestions.isLoading || list.length === 0) return null;

  // One at a time, so a clash with an existing mapping stops at that suggestion with its reason
  // rather than half the batch failing silently.
  const addAllHigh = async () => {
    setAddingAll(true);
    try {
      for (const s of high) await acceptSuggestion.mutateAsync(s.id);
    } catch {
      // The mutation's own handler has already said what went wrong.
    } finally {
      setAddingAll(false);
    }
  };

  return (
    <section className="space-y-2 rounded-md border border-dashed border-gray-300 bg-gray-50 p-3" aria-label="Suggestions">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Suggestions · not saved until you add them</p>
        {high.length > 1 && (
          <Button variant="outlined" size="sm" onClick={addAllHigh} disabled={addingAll}>
            Add {high.length} high confidence
          </Button>
        )}
      </div>

      <ul className="space-y-1.5">
        {list.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-3 rounded border border-gray-200 bg-white px-3 py-2 text-sm">
            <span className={clsx("rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset", confidenceMeta[s.confidence])}>
              {s.confidence}
            </span>
            <span className="min-w-0 flex-1">
              <span className="text-gray-600">{describeConditions(s)}</span> →{" "}
              <span className="font-medium text-gray-900">{s.partCategoryName ?? "Deleted category"}</span>
              {s.reason && <span className="block text-xs text-gray-500">{s.reason}</span>}
            </span>
            <span className="flex gap-1">
              <Button size="sm" onClick={() => acceptSuggestion.mutate(s.id)} disabled={acceptSuggestion.isPending || addingAll}>
                Add
              </Button>
              <Button variant="outlined" size="sm" onClick={() => dismissSuggestion.mutate(s.id)} disabled={dismissSuggestion.isPending}>
                Dismiss
              </Button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
